// tools/levels_sync.py (DEPTH.md §8): the beats signature and what a client does with a depth_levels row.
import type { Structure } from "../levels/types";

export function beatsSig(s: Structure): string {
  return s.beats.map((b) => `${b.from}-${b.to}`).join(",");
}

export interface LevelsRow {
  item_key: string; chapter: number; seq: number; paragraphs: number; beats_sig: string; structure: string; notes: string;
}

export interface Outcome {
  action: "ignore" | "take";
  structure: Structure | null;
  notes: Record<number, string>;
  push_notes: boolean;
  changed: boolean;
}

/** JSON equality as Python's == on parsed values (key order doesn't matter). */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) {
    const bb = b as unknown[];
    return a.length === bb.length && a.every((x, i) => deepEqual(x, bb[i]));
  }
  const ka = Object.keys(a as object), kb = Object.keys(b as object);
  return ka.length === kb.length && ka.every((k) => Object.prototype.hasOwnProperty.call(b, k) && deepEqual((a as any)[k], (b as any)[k]));
}

export function decodeNotes(json: string | null | undefined): Record<number, string> {
  const out: Record<number, string> = {};
  try {
    const obj = JSON.parse(json || "{}");
    for (const [k, v] of Object.entries(obj)) if (/^\d+$/.test(k) && typeof v === "string") out[parseInt(k, 10)] = v;
  } catch { /* empty */ }
  return out;
}

/** {beat: note} → the row's JSON {"<beat>": note}, keys in numeric order. */
export function encodeNotes(notes: Record<number, string>): string {
  const keys = Object.keys(notes).map(Number).sort((a, b) => a - b);
  const obj: Record<string, string> = {};
  for (const k of keys) obj[String(k)] = notes[k];
  return JSON.stringify(obj);
}

/** The structure JSON with sorted keys, like the apps (field names are the same on every platform). */
export function encodeStructure(s: Structure): string {
  const sorted = (v: any): any => Array.isArray(v) ? v.map(sorted)
    : v && typeof v === "object" ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, sorted(v[k])])) : v;
  return JSON.stringify(sorted(s));
}

export function applyRow(localStructure: Structure | null, localNotes: Record<number, string>, row: LevelsRow, paragraphs: number): Outcome {
  if (row.paragraphs !== paragraphs) {
    return { action: "ignore", structure: localStructure, notes: localNotes, push_notes: false, changed: false };
  }
  let remote: Structure;
  try { remote = JSON.parse(row.structure); } catch {
    return { action: "ignore", structure: localStructure, notes: localNotes, push_notes: false, changed: false };
  }
  const notes: Record<number, string> = { ...decodeNotes(row.notes) };
  let push = false;
  if (localStructure && beatsSig(localStructure) === row.beats_sig) {
    for (const [k, n] of Object.entries(localNotes)) {
      if (!(k in notes)) { notes[Number(k)] = n; push = true; }
    }
  }
  const changed = !deepEqual(remote, localStructure) || !deepEqual(notes, localNotes);
  return { action: "take", structure: remote, notes, push_notes: push, changed };
}
